import type { Step5RoomTruthV1, StudioApiClient } from "../../api.js";
import { clientLinkUrl as buildClientLinkUrl } from "../../client-link-url.js";
import type { StudioRuntimeConfig } from "../../env.js";
import {
  agencyEditedDescriptionDraftUpdate,
  candidateGraphFromRoomIntake,
  confirmDraftIdempotencyKey,
  confirmedBuildIdempotencyKey,
  draftNeedsReconfirmation,
  expectedPredecessorRevisionIdFromRoomTruth,
  shouldOfferCreateClientLink,
  shouldOfferReissueClientLink,
  startBuildBlockedReason,
} from "./greenfield-truth.js";

export type MaterialActionResult =
  | { readonly ok: true; readonly alreadyExisted?: boolean; readonly reissued?: boolean }
  | { readonly ok: false; readonly error: string };

type RoomApi = Pick<
  StudioApiClient,
  | "getStep5RoomTruth"
  | "createClientLink"
  | "reissueClientLink"
  | "createInitialCurrentDraft"
  | "confirmBusinessGraphDraft"
  | "startConfirmedBuild"
  | "updateBusinessGraphDraft"
>;

type DeliveryApi = Pick<StudioApiClient, "getStep5RoomTruth" | "patchDeliveryDueAt" | "requestRunExport">;

export async function markChangeRequestInProgressOnce(input: {
  readonly api: Pick<StudioApiClient, "getStep5RoomTruth" | "transitionChangeRequest">;
  readonly projectId: string;
  readonly inFlight: { current: boolean };
}): Promise<MaterialActionResult> {
  if (input.inFlight.current) {
    return { ok: false, error: "Change request update is already in progress." };
  }
  input.inFlight.current = true;
  try {
    const current = await input.api.getStep5RoomTruth(input.projectId);
    const changeRequest = current.changeRequest;
    if (!changeRequest) {
      return { ok: false, error: "No client change request is waiting." };
    }
    if (changeRequest.status === "IN_PROGRESS") {
      return { ok: true, alreadyExisted: true };
    }
    if (changeRequest.status !== "SUBMITTED") {
      return { ok: false, error: `Change request is ${changeRequest.status.toLowerCase()} and cannot start.` };
    }
    await input.api.transitionChangeRequest(
      input.projectId,
      changeRequest.changeRequestId,
      {
        schemaId: "agency.changeRequest.transition.v1",
        targetStatus: "IN_PROGRESS",
      },
      `gf-cr-ip-${changeRequest.changeRequestId}`,
    );
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "change_request_transition_failed" };
  } finally {
    input.inFlight.current = false;
  }
}

export async function resolveChangeRequestWithLatestPreviewOnce(input: {
  readonly api: Pick<StudioApiClient, "getStep5RoomTruth" | "getClientReviewStatus" | "transitionChangeRequest">;
  readonly projectId: string;
  readonly inFlight: { current: boolean };
}): Promise<MaterialActionResult> {
  if (input.inFlight.current) {
    return { ok: false, error: "Change request resolution is already in progress." };
  }
  input.inFlight.current = true;
  try {
    const current = await input.api.getStep5RoomTruth(input.projectId);
    const changeRequest = current.changeRequest;
    if (!changeRequest) {
      return { ok: false, error: "No client change request is waiting." };
    }
    if (changeRequest.status === "RESOLVED") {
      return { ok: true, alreadyExisted: true };
    }
    if (changeRequest.status !== "IN_PROGRESS") {
      return { ok: false, error: "Mark the change request in progress before binding a revised preview." };
    }
    const review = await input.api.getClientReviewStatus(input.projectId);
    const previewVersionId = review.latestPreview?.previewVersionId;
    if (!previewVersionId || previewVersionId.length !== 64) {
      return {
        ok: false,
        error: "A revised client-visible preview is not ready yet. Start a new build and wait for quality gate.",
      };
    }
    await input.api.transitionChangeRequest(
      input.projectId,
      changeRequest.changeRequestId,
      {
        schemaId: "agency.changeRequest.transition.v1",
        targetStatus: "RESOLVED",
        previewVersionId,
      },
      `gf-cr-res-${changeRequest.changeRequestId}-${previewVersionId.slice(0, 12)}`,
    );
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "change_request_resolve_failed" };
  } finally {
    input.inFlight.current = false;
  }
}

export async function saveBusinessDescriptionDraftOnce(input: {
  readonly api: Pick<StudioApiClient, "getStep5RoomTruth" | "updateBusinessGraphDraft">;
  readonly projectId: string;
  readonly description: string;
  readonly inFlight: { current: boolean };
}): Promise<MaterialActionResult> {
  if (input.inFlight.current) {
    return { ok: false, error: "Business description save is already in progress." };
  }
  input.inFlight.current = true;
  try {
    const current = await input.api.getStep5RoomTruth(input.projectId);
    const changeRequest = current.changeRequest;
    if (!changeRequest) {
      return { ok: false, error: "No client change request is waiting." };
    }
    if (changeRequest.status !== "IN_PROGRESS") {
      return { ok: false, error: "Mark the change request in progress before editing the business description." };
    }
    const update = agencyEditedDescriptionDraftUpdate(input.description);
    if ("error" in update) {
      return { ok: false, error: update.error };
    }
    if (!current.draft) {
      return { ok: false, error: "A current draft is required before the business description can be saved." };
    }
    await input.api.updateBusinessGraphDraft(input.projectId, current.draft.draftId, {
      description: update.description,
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "business_description_save_failed" };
  } finally {
    input.inFlight.current = false;
  }
}

export async function createClientAccessOnce(input: {
  readonly api: RoomApi;
  readonly projectId: string;
  readonly config: StudioRuntimeConfig;
  readonly inFlight: { current: boolean };
}): Promise<MaterialActionResult & { readonly linkUrl?: string }> {
  if (input.inFlight.current) {
    return { ok: false, error: "Client access is already being created." };
  }
  input.inFlight.current = true;
  try {
    const current = await input.api.getStep5RoomTruth(input.projectId);
    if (shouldOfferCreateClientLink(current.clientAccess.exists)) {
      const created = await input.api.createClientLink(input.projectId);
      return { ok: true, linkUrl: buildClientLinkUrl(input.config, created.shellPath) };
    }
    if (!shouldOfferReissueClientLink(current.clientAccess.exists)) {
      return { ok: false, error: "Client access cannot be created or reissued." };
    }
    const reissued = await input.api.reissueClientLink(input.projectId);
    return {
      ok: true,
      reissued: true,
      linkUrl: buildClientLinkUrl(input.config, reissued.shellPath),
    };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "client_access_failed" };
  } finally {
    input.inFlight.current = false;
  }
}

export async function confirmCandidateOnce(input: {
  readonly api: RoomApi;
  readonly agencyId: string;
  readonly projectId: string;
  readonly selectedLensId?: "lens-flat-2" | "lens-glass";
  readonly inFlight: { current: boolean };
}): Promise<MaterialActionResult> {
  if (input.inFlight.current) {
    return { ok: false, error: "Confirmation is already in progress." };
  }
  input.inFlight.current = true;
  try {
    let current = await input.api.getStep5RoomTruth(input.projectId);
    if (!current.intake?.submitted) {
      return { ok: false, error: "Client intake is not submitted yet. Confirmation is closed." };
    }
    if (current.confirmationId && !draftNeedsReconfirmation(current.draft)) {
      return { ok: true, alreadyExisted: true };
    }
    if (!current.draft) {
      const graph = candidateGraphFromRoomIntake({
        agencyId: input.agencyId,
        projectId: input.projectId,
        businessName: current.intake.businessName || current.businessName || "This business",
        primaryPhone: current.intake.primaryPhone,
        primaryEmail: current.intake.primaryEmail,
        description: current.intake.description,
        services: current.intake.services,
        serviceAreas: current.intake.serviceAreas,
        primaryEvidenceType: current.intake.primaryEvidenceType,
        sitePackageId: current.sitePackageId,
        lensId: input.selectedLensId,
      });
      await input.api.createInitialCurrentDraft(input.projectId, graph, `gf-draft-${input.projectId}`);
      current = await input.api.getStep5RoomTruth(input.projectId);
    }
    if (!current.draft) {
      return { ok: false, error: "A reviewable candidate draft could not be created." };
    }
    await input.api.confirmBusinessGraphDraft(input.projectId, current.draft.draftId, {
      expectedDraftVersion: current.draft.draftVersion,
      expectedDraftSnapshotHash: current.draft.draftSnapshotHash,
      expectedPredecessorRevisionId: expectedPredecessorRevisionIdFromRoomTruth(current),
      idempotencyKey: confirmDraftIdempotencyKey({
        projectId: input.projectId,
        draftId: current.draft.draftId,
        draftVersion: current.draft.draftVersion,
        draftSnapshotHash: current.draft.draftSnapshotHash,
      }),
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "confirm_failed" };
  } finally {
    input.inFlight.current = false;
  }
}

export async function startConfirmedBuildOnce(input: {
  readonly api: RoomApi;
  readonly projectId: string;
  readonly inFlight: { current: boolean };
}): Promise<MaterialActionResult> {
  if (input.inFlight.current) {
    return { ok: false, error: "Start build is already in progress." };
  }
  input.inFlight.current = true;
  try {
    const current = await input.api.getStep5RoomTruth(input.projectId);
    const blocked =
      current.startBuildBlocker ??
      startBuildBlockedReason({
        confirmationId: current.confirmationId,
        draftId: current.draft?.draftId,
        draftStatus: current.draft?.status,
        latestRunStatus: current.latestRun?.status,
      });
    if (blocked) return { ok: false, error: blocked };
    if (!current.confirmationId || !current.draft) {
      return { ok: false, error: "Start build is closed until a confirmed candidate exists." };
    }
    await input.api.startConfirmedBuild(
      input.projectId,
      current.confirmationId,
      {
        mode: "FRESH_BUILD",
        expectedCurrentDraftId: current.draft.draftId,
        expectedCurrentDraftVersion: current.draft.draftVersion,
        expectedCurrentDraftSnapshotHash: current.draft.draftSnapshotHash,
        expectedPredecessorRevisionId: expectedPredecessorRevisionIdFromRoomTruth(current),
        expectedGenerationSelectionVersion: current.generationSelectionVersion,
      },
      confirmedBuildIdempotencyKey({
        projectId: input.projectId,
        confirmationId: current.confirmationId,
        draftId: current.draft.draftId,
        draftVersion: current.draft.draftVersion,
        draftSnapshotHash: current.draft.draftSnapshotHash,
      }),
    );
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "start_build_failed" };
  } finally {
    input.inFlight.current = false;
  }
}

export async function prepareDeliveryOnce(input: {
  readonly api: DeliveryApi;
  readonly projectId: string;
  readonly inFlight: { current: boolean };
}): Promise<MaterialActionResult> {
  if (input.inFlight.current) {
    return { ok: false, error: "Delivery preparation is already in progress." };
  }
  input.inFlight.current = true;
  try {
    const current = await input.api.getStep5RoomTruth(input.projectId);
    if (!current.approval?.approvalId) {
      return { ok: false, error: "Exact client approval is required before delivery can be prepared." };
    }
    if (current.deliveryDueAt) {
      return { ok: true, alreadyExisted: true };
    }
    if (typeof current.lifecycleVersion !== "number") {
      return { ok: false, error: "Delivery due cannot be set until the project lifecycle version is known." };
    }
    const deliveryDueAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    await input.api.patchDeliveryDueAt(
      input.projectId,
      {
        deliveryDueAt,
        expectedLifecycleVersion: current.lifecycleVersion,
      },
      `gf-delivery-${input.projectId}-${current.lifecycleVersion}`,
    );
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "prepare_delivery_failed" };
  } finally {
    input.inFlight.current = false;
  }
}

export async function packageHandoffOnce(input: {
  readonly api: DeliveryApi;
  readonly projectId: string;
  readonly inFlight: { current: boolean };
}): Promise<MaterialActionResult> {
  if (input.inFlight.current) {
    return { ok: false, error: "Handoff packaging is already in progress." };
  }
  input.inFlight.current = true;
  try {
    const current = await input.api.getStep5RoomTruth(input.projectId);
    if (!current.approval?.approvalId) {
      return { ok: false, error: "Exact client approval is required before packaging handoff." };
    }
    if (!current.deliveryDueAt) {
      return { ok: false, error: "Prepare delivery before packaging the ownership handoff." };
    }
    const runId = current.approval.runId || current.latestRun?.runId;
    if (!runId) {
      return { ok: false, error: "No approved preview run is available to package." };
    }
    await input.api.requestRunExport(runId, input.projectId);
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "package_handoff_failed" };
  } finally {
    input.inFlight.current = false;
  }
}

export function roomTruthShowsQueuedBuild(truth: Step5RoomTruthV1 | null): boolean {
  return Boolean(truth?.latestRun && ["QUEUED", "RUNNING", "PENDING"].includes(truth.latestRun.status));
}
